import {MimeType} from '@coolcolduk/enum';
import {Dropzone, useMessage} from '@coolcolduk/ui-mui';
import {fileToBase64ObjectNoFileReader} from '@coolcolduk/util';
import {Alert, AlertTitle, Box, Button, TextField, Typography} from '@mui/material';
import {useState} from 'react';
import useCreateFlashcardByCsv from '../../hooks/useCreateFlashcardByCsv';

/**
 * Download a base64 file
 * @param base64Data Base64 data string
 * @param fileName File name
 * @param mimeType MIME type
 */
const downloadBase64File = (base64Data: string, fileName: string, mimeType: string) => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  byteCharacters.split('').forEach((char, i) => {
    byteNumbers[i] = char.charCodeAt(0);
  });
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {type: mimeType});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function FlashCard() {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [title, setTitle] = useState('');
  const [generatedPdf, setGeneratedPdf] = useState<{data: string; name: string; type: string} | null>(null);
  const {showMessage} = useMessage();
  const {createFlashcardByCsv, loading: isGenerating} = useCreateFlashcardByCsv();

  const handleFileUpload = async (uploadedFiles: File[]) => {
    setFile(uploadedFiles[0]);
    // Clear previous generated PDF when new file is uploaded
    setGeneratedPdf(null);
  };

  const handleDownload = async () => {
    if (!file) return;

    try {
      // Convert file to base64
      const csvFile = await fileToBase64ObjectNoFileReader(file);

      // Call backend service
      const result = await createFlashcardByCsv(csvFile, title);

      if (result) {
        // Store PDF data in memory
        setGeneratedPdf({
          data: result.data,
          name: result.name,
          type: result.type,
        });

        // Auto-download the PDF
        downloadBase64File(result.data, result.name, result.type);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      showMessage('Failed to generate flashcard PDF. Please try again.', 6000, 'error');
    }
  };

  const handleManualDownload = () => {
    if (generatedPdf) {
      downloadBase64File(generatedPdf.data, generatedPdf.name, generatedPdf.type);
    }
  };

  return (
    <Box sx={{p: 4}}>
      <Typography variant="h4" component="h1" gutterBottom>
        Generate Flashcards
      </Typography>

      <Alert severity="info" sx={{mb: 3}}>
        <AlertTitle>Instructions</AlertTitle>
        <Typography variant="body2" component="div">
          <strong>To generate flashcards, you need:</strong>
          <br />
          • A CSV or Excel file with two columns: Question and Answer
          <br />
          • Each row represents one flashcard
          <br />
          • No header row required
          <br />• Supported formats: .csv, .xlsx, .xls
        </Typography>
      </Alert>

      <Typography variant="h6" gutterBottom>
        Step 1: Enter a title for your flashcards
      </Typography>
      <TextField
        fullWidth
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          // Clear previous generated PDF when title changes
          setGeneratedPdf(null);
        }}
        placeholder="Enter title (e.g., 'Math Formulas', 'History Facts')"
        sx={{mb: 3}}
      />

      <Typography variant="h6" gutterBottom>
        Step 2: Upload your CSV or Excel file
      </Typography>
      <Dropzone
        files={file ? [file] : []}
        setFiles={handleFileUpload}
        acceptedFileTypes={[MimeType.TEXT_CSV, MimeType.APPLICATION_VND_MS_EXCEL]}
        withPreview
      />

      <Typography variant="h6" gutterBottom sx={{mt: 3}}>
        Step 3: Generate your flashcards
      </Typography>
      <Box sx={{mt: 2}}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          disabled={isGenerating || !file || !title.trim()}
          size="large"
        >
          {isGenerating ? 'Generating PDF...' : 'Download Flashcards PDF'}
        </Button>
      </Box>

      {file && title.trim() && !generatedPdf && (
        <Alert severity="success" sx={{mt: 2}}>
          <AlertTitle>Ready to generate!</AlertTitle>
          Your flashcards will be saved as: <strong>{title}_flashcards.pdf</strong>
        </Alert>
      )}

      {generatedPdf && (
        <Alert severity="success" sx={{mt: 2}}>
          <AlertTitle>PDF Generated Successfully!</AlertTitle>
          <Typography variant="body2" component="div">
            Your flashcard PDF has been generated and download should have started automatically.
            <br />
            If the download didn&apos;t start, you can{' '}
            <Button
              variant="text"
              color="primary"
              onClick={handleManualDownload}
              sx={{p: 0, minWidth: 'auto', textDecoration: 'underline'}}
            >
              click here to download
            </Button>{' '}
            the file: <strong>{generatedPdf.name}</strong>
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
